/**
 * Page header component.
 * Provides consistent header styling with title and optional actions.
 * Mobile-first responsive design.
 */

import { ChevronRight, Shield } from "lucide-react";
import { Link } from "react-router-dom";
import { KiiEditableTitle } from "@/components/kii/kii-editable-title";

interface BreadcrumbItem {
  label: string;
  href?: string;
}

interface PageHeaderProps {
  title: string;
  description?: string;
  breadcrumbs?: BreadcrumbItem[];
  actions?: React.ReactNode;
  /** Show neutral admin indicator */
  isAdmin?: boolean;
  /** When provided, title becomes inline-editable (Enter to save, Escape to cancel) */
  onTitleSave?: (newTitle: string) => void;
}

export function PageHeader({
  title,
  description,
  breadcrumbs,
  actions,
  isAdmin,
  onTitleSave,
}: PageHeaderProps) {
  return (
    <div className="mb-4 lg:mb-6">
      {/* Admin indicator badge */}
      {isAdmin && (
        <div className="flex items-center gap-2 px-3 py-1.5 mb-3 bg-muted border border-border rounded-lg text-muted-foreground text-sm">
          <Shield className="h-4 w-4" />
          <span className="font-medium">Admin</span>
        </div>
      )}

      {/* Breadcrumbs - scrollable on mobile */}
      {breadcrumbs && breadcrumbs.length > 0 && (
        <nav className="flex items-center gap-1 text-xs text-muted-foreground mb-2 overflow-x-auto whitespace-nowrap pb-1">
          {breadcrumbs.map((item, index) => (
            <span key={index} className="flex items-center gap-1 shrink-0">
              {index > 0 && <ChevronRight className="h-3.5 w-3.5" />}
              {item.href ? (
                <Link
                  to={item.href}
                  className="hover:text-primary transition-colors"
                >
                  {item.label}
                </Link>
              ) : (
                <span className="text-foreground">{item.label}</span>
              )}
            </span>
          ))}
        </nav>
      )}

      {/* Title and Actions - always row layout with actions on right */}
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0 flex-1">
          <KiiEditableTitle
            value={title}
            onSave={onTitleSave}
            className="text-3xl sm:text-4xl font-bold"
          />
          {description && (
            <p className="mt-1 text-sm sm:text-base text-muted-foreground line-clamp-2">
              {description}
            </p>
          )}
        </div>
        {actions && (
          <div className="flex items-center gap-2 shrink-0">{actions}</div>
        )}
      </div>
    </div>
  );
}
